import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Activity, Bot, Trophy, Receipt, ShoppingBag,
  UserPlus, Server, Menu, X, LogOut, Home, ShieldCheck, CreditCard, Eye,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

import AdminDashboard from "../components/admin/AdminDashboard";
import AdminUsers from "../components/admin/AdminUsers";
import AdminMonitoring from "../components/admin/AdminMonitoring";
import AdminPredictions from "../components/admin/AdminPredictions";
import AdminLotteries from "../components/admin/AdminLotteries";
import AdminTransactions from "../components/admin/AdminTransactions";
import AdminAffiliates from "../components/admin/AdminAffiliates";
import AdminSystem from "../components/admin/AdminSystem";
import AdminPayments from "../components/admin/AdminPayments";
import AdminVisits from "../components/admin/AdminVisits";

const NAV = [
  { id: "dashboard",     label: "Dashboard",     icon: LayoutDashboard },
  { id: "visits",        label: "Visitas",       icon: Eye, pro: true },
  { id: "users",         label: "Usuarios",      icon: Users },
  { id: "monitoring",    label: "Monitoreo",     icon: Activity, pro: true },
  { id: "predictions",   label: "Predicciones",  icon: Bot, pro: true },
  { id: "lotteries",     label: "Loterías",      icon: Trophy },
  { id: "results",       label: "Resultados",    icon: Receipt },
  { id: "payments",      label: "Pagos",         icon: CreditCard, pro: true },
  { id: "transactions",  label: "Transacciones", icon: ShoppingBag },
  { id: "affiliates",    label: "Afiliados",     icon: UserPlus },
  { id: "system",        label: "Sistema",       icon: Server },
];

export default function Admin() {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [openMobile, setOpenMobile] = useState(false);

  if (loading) return <div className="p-10 text-center text-gray-500">Cargando...</div>;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white rounded-2xl border border-red-100 shadow p-8">
          <ShieldCheck className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Acceso restringido</h1>
          <p className="text-sm text-gray-600 mb-5">Esta área es exclusiva para administradores.</p>
          <Link to="/" className="inline-block px-5 py-2 rounded-full bg-[#1d6cb1] text-white font-semibold">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const renderTab = () => {
    switch (tab) {
      case "dashboard":    return <AdminDashboard />;
      case "visits":       return <AdminVisits />;
      case "users":        return <AdminUsers />;
      case "monitoring":   return <AdminMonitoring />;
      case "predictions":  return <AdminPredictions />;
      case "lotteries":    return <AdminLotteries />;
      case "results":      return <AdminLotteries resultsTab />;
      case "transactions": return <AdminTransactions />;
      case "payments":     return <AdminPayments />;
      case "affiliates":   return <AdminAffiliates />;
      case "system":       return <AdminSystem />;
      default:             return <AdminDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      {/* Top bar (mobile) */}
      <div className="lg:hidden bg-[#0e1b4a] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpenMobile(true)} className="p-1.5 rounded bg-white/10"><Menu className="w-5 h-5" /></button>
          <span className="font-extrabold">Admin · Números Fácil</span>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${openMobile ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 lg:z-auto h-screen lg:h-auto lg:min-h-screen w-64 bg-gradient-to-b from-[#0e1b4a] via-[#0e2d6a] to-[#0e4a7a] text-white shadow-xl transition-transform`}>
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-white/95 p-0.5 flex items-center justify-center">
                <img src="https://customer-assets.emergentagent.com/job_numero-builder/artifacts/yhm96yb7_VANDIERE.png" alt="logo" className="w-full h-full object-contain" />
              </div>
              <div className="leading-tight">
                <p className="font-extrabold">Admin Panel</p>
                <p className="text-[10px] text-blue-200">Números Fácil</p>
              </div>
            </div>
            <button className="lg:hidden p-1 rounded hover:bg-white/10" onClick={() => setOpenMobile(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="p-3 space-y-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = tab === n.id;
              return (
                <button
                  key={n.id}
                  onClick={() => { setTab(n.id); setOpenMobile(false); }}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                    active ? "bg-white/15 text-white shadow" : "text-blue-100 hover:bg-white/10"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{n.label}</span>
                  {n.pro && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-400 text-[#0e1b4a] text-[9px] font-extrabold">PRO</span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="p-3 mt-2 border-t border-white/10">
            <div className="px-3 py-2 mb-1">
              <p className="text-xs text-blue-200">Sesión</p>
              <p className="font-semibold text-sm truncate">{user.name}</p>
              <p className="text-[10px] text-blue-200 truncate">{user.email}</p>
            </div>
            <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-blue-100 hover:bg-white/10">
              <Home className="w-4 h-4" /> Ir al sitio
            </Link>
            <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-200 hover:bg-red-500/20">
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          </div>
        </aside>

        {/* Backdrop mobile */}
        {openMobile && <div onClick={() => setOpenMobile(false)} className="lg:hidden fixed inset-0 bg-black/40 z-30" />}

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 py-6 lg:py-8">
          <div className="max-w-7xl mx-auto">{renderTab()}</div>
        </main>
      </div>
    </div>
  );
}
